import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, Button, Card, Code, Grid, Group, Stack, Text } from "@mantine/core";
import { IconCloudUpload, IconEye, IconHistory, IconRefresh } from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { Detail, ModelVersion } from "../../types/api";
import { formatDateTime } from "../../lib/format";
import GlbViewer, { type ViewerShading } from "../viewer/GlbViewer";
import ModelThumbs from "../models/ModelThumbs";
import type { ModelPresenter } from "../models/presenter";
import { useAsyncAction } from "../../lib/useAsyncAction";
import { ColorDot, ConfirmButton, DownloadButton, ErrorState, ImageZoom, Page } from "../ui";

type VersionedModel = { id: string; url: string; previousVersions: ModelVersion[] };

interface DetailPageProps<TModel extends VersionedModel> {
  title: string;
  /** Where "back" goes when there is no history. */
  galleryPath: string;
  data: Detail<TModel>;
  presenter: ModelPresenter<TModel>;
  shading: ViewerShading;
  /** The "generate another variant" form, rendered above the model cards. */
  addVariant: ReactNode;
  /** Re-runs generation for one existing model (same variant) and returns the updated aggregate. */
  onRegenerateModel: (model: TModel) => Promise<Detail<TModel>>;
  onDataChange: (data: Detail<TModel>) => void;
  onDelete: () => Promise<void>;
}

/**
 * The shared detail page for a generated aggregate (pillow, ornament): original image with its
 * actions, the variant form + one card per model, the 3D viewer with a picker, and the raw JSON.
 * Everything kind-specific comes through the presenter and the two callbacks.
 */
export default function DetailPage<TModel extends VersionedModel>({
  title,
  galleryPath,
  data,
  presenter,
  shading,
  addVariant,
  onRegenerateModel,
  onDataChange,
  onDelete,
}: DetailPageProps<TModel>) {
  const navigate = useNavigate();
  const [selectedModelId, setSelectedModelId] = useState<string | null>(data.models[0]?.id ?? null);
  // A previous version chosen from a model's history; cleared whenever a model is picked.
  const [viewedVersion, setViewedVersion] = useState<ModelVersion | null>(null);
  const regenerate = useAsyncAction(async (model: TModel) => onDataChange(await onRegenerateModel(model)));
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);

  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate(galleryPath));
  const selectedModel = data.models.find((m) => m.id === selectedModelId) ?? data.models[0] ?? null;
  const viewerUrl = viewedVersion?.url ?? selectedModel?.url ?? null;

  const selectModel = (id: string) => {
    setSelectedModelId(id);
    setViewedVersion(null);
  };

  const viewVersion = (model: TModel, version: ModelVersion) => {
    setSelectedModelId(model.id);
    setViewedVersion(version);
  };

  const regenerateModel = async (model: TModel) => {
    setRegeneratingId(model.id);
    await regenerate.run(model);
    setRegeneratingId(null);
  };

  return (
    <Page
      title={title}
      onBack={goBack}
      actions={
        <ConfirmButton
          message={`Delete this ${title.toLowerCase()} and all of its models? The original image stays in storage.`}
          onConfirm={async () => {
            await onDelete();
            navigate(galleryPath);
          }}
        />
      }
    >
      <Accordion multiple defaultValue={["viewer"]} variant="separated">
        <Accordion.Item value="original">
          <Accordion.Control>
            <Text fw={600}>Original Image</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Grid>
              <Grid.Col span={{ base: 12, xs: 6, sm: 4, md: 3 }}>
                <Stack gap="sm">
                  <ImageZoom src={data.image.url} alt="original" withBorder={false} />
                  <DownloadButton href={data.image.url} fullWidth />
                  <Button
                    variant="light"
                    leftSection={<IconCloudUpload size={16} />}
                    size="xs"
                    fullWidth
                    onClick={() =>
                      navigate("/generate/serverless", {
                        state: { prefill: { imageUrl: data.image.url, fileName: `${title.toLowerCase()}-${data.image.id}.png` } },
                      })
                    }
                  >
                    Send to serverless
                  </Button>
                </Stack>
              </Grid.Col>
            </Grid>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="models">
          <Accordion.Control>
            <Text fw={600}>Models ({data.models.length})</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              {addVariant}
              {regenerate.error && <ErrorState message={regenerate.error} title="Regeneration failed" />}
              <Grid>
                {data.models.map((model) => {
                  const { src, tint, label } = presenter.thumb(model);
                  return (
                    <Grid.Col key={model.id} span={{ base: 12, xs: 6, sm: 4, md: 3 }}>
                      <Card padding="xs">
                        <Stack gap="xs">
                          <Group gap="xs" wrap="nowrap">
                            <ColorDot color={tint} />
                            <Text size="xs" c="dimmed" truncate>
                              {label}
                            </Text>
                          </Group>
                          {presenter.badges?.(model)}
                          {src && <ImageZoom src={src} radius="sm" background={tint} />}
                          <Group gap="xs">
                            <DownloadButton href={model.url} label="glTF" style={{ flex: 1 }} />
                            {presenter.downloads(model).map((d) => (
                              <DownloadButton key={d.label} href={d.url} label={d.label} style={{ flex: 1 }} />
                            ))}
                          </Group>
                          <Button
                            variant="subtle"
                            size="xs"
                            leftSection={<IconRefresh size={14} />}
                            loading={regeneratingId === model.id}
                            disabled={regenerate.busy}
                            onClick={() => void regenerateModel(model)}
                          >
                            Regenerate
                          </Button>
                          {model.previousVersions.length > 0 && (
                            <Accordion variant="contained" chevronPosition="left">
                              <Accordion.Item value="history">
                                <Accordion.Control icon={<IconHistory size={14} />} py={4}>
                                  <Text size="xs">History ({model.previousVersions.length})</Text>
                                </Accordion.Control>
                                <Accordion.Panel>
                                  <Stack gap="xs">
                                    {model.previousVersions.map((version) => (
                                      <Group key={version.id} gap="xs" wrap="nowrap" align="center">
                                        {version.previewUrl && (
                                          <ImageZoom src={version.previewUrl} w={36} h={36} radius="sm" background={tint} />
                                        )}
                                        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                                          <Text size="xs" truncate>
                                            {formatDateTime(version.createdAt)}
                                          </Text>
                                          <Text size="xs" c="dimmed" truncate>
                                            until {formatDateTime(version.supersededAt)}
                                          </Text>
                                        </Stack>
                                        <Button
                                          variant={viewedVersion?.id === version.id ? "light" : "subtle"}
                                          size="compact-xs"
                                          leftSection={<IconEye size={12} />}
                                          onClick={() => viewVersion(model, version)}
                                        >
                                          View
                                        </Button>
                                        <DownloadButton href={version.url} label="glTF" size="compact-xs" />
                                      </Group>
                                    ))}
                                  </Stack>
                                </Accordion.Panel>
                              </Accordion.Item>
                            </Accordion>
                          )}
                        </Stack>
                      </Card>
                    </Grid.Col>
                  );
                })}
              </Grid>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {selectedModel && viewerUrl && (
          <Accordion.Item value="viewer">
            <Accordion.Control>
              <Text fw={600}>3D Viewer</Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {data.models.length > 1 && (
                  <ModelThumbs models={data.models} presenter={presenter} selectedId={selectedModel.id} onSelect={selectModel} size={68} />
                )}
                {viewedVersion && (
                  <Group gap="xs">
                    <Text size="sm" c="dimmed">
                      Showing the version from {formatDateTime(viewedVersion.createdAt)}.
                    </Text>
                    <Button variant="subtle" size="compact-xs" onClick={() => setViewedVersion(null)}>
                      Back to current
                    </Button>
                  </Group>
                )}
                <GlbViewer key={viewerUrl} url={viewerUrl} shading={shading} />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        <Accordion.Item value="raw">
          <Accordion.Control>
            <Text fw={600}>Raw</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Code block style={{ maxHeight: 300, overflow: "auto" }}>
              {JSON.stringify(data, null, 2)}
            </Code>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Page>
  );
}
